import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Admin = () => {
  const [galleryImages, setGalleryImages] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [editingImage, setEditingImage] = useState(null);
  const [newImage, setNewImage] = useState({
    category: 'wedding',
    image_url: '',
    alt_text: '',
    caption_it: '',
    caption_en: '',
    order: 0,
    is_featured: false
  });

  useEffect(() => {
    fetchGallery();
    fetchContacts();
  }, []);

  const fetchGallery = async () => {
    try {
      const response = await axios.get(`${API}/gallery`);
      setGalleryImages(response.data);
    } catch (error) {
      console.error('Error fetching gallery:', error);
    }
  };

  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  };

  const handleFileUpload = async () => {
    if (!uploadFile) {
      toast.error('Please select a file');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setNewImage(prev => ({ ...prev, image_url: response.data.url }));
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Error uploading file');
    }
  };

  const handleAddImage = async () => {
    if (!newImage.image_url) {
      toast.error('Aggiungi un URL immagine o carica un file');
      return;
    }

    try {
      await axios.post(`${API}/gallery`, newImage);
      toast.success('Immagine aggiunta con successo');
      fetchGallery();
      setNewImage({
        category: 'wedding',
        image_url: '',
        alt_text: '',
        caption_it: '',
        caption_en: '',
        order: 0,
        is_featured: false
      });
      setUploadFile(null);
    } catch (error) {
      console.error('Error adding image:', error);
      toast.error('Errore nell\'aggiungere l\'immagine');
    }
  };

  const handleDeleteImage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    try {
      await axios.delete(`${API}/gallery/${id}`);
      toast.success('Image deleted successfully');
      fetchGallery();
    } catch (error) {
      console.error('Error deleting image:', error);
      toast.error('Error deleting image');
    }
  };

  const handleEditImage = (image) => {
    setEditingImage(image);
  };

  const handleUpdateImage = async () => {
    if (!editingImage) return;

    try {
      await axios.put(`${API}/gallery/${editingImage.id}`, {
        alt_text: editingImage.alt_text,
        caption_it: editingImage.caption_it,
        caption_en: editingImage.caption_en,
        order: editingImage.order,
        is_featured: editingImage.is_featured
      });
      toast.success('Image updated successfully');
      setEditingImage(null);
      fetchGallery();
    } catch (error) {
      console.error('Error updating image:', error);
      toast.error('Error updating image');
    }
  };

  return (
    <div data-testid="admin-page" className="pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6">
        <h1
          className="text-5xl font-light tracking-tight leading-tight mb-8"
          style={{ fontFamily: 'Cormorant Garamond, serif' }}
          data-testid="admin-title"
        >
          Admin Panel
        </h1>

        <Tabs defaultValue="gallery" className="w-full">
          <TabsList className="grid w-full max-w-md grid-cols-2 mb-8" data-testid="admin-tabs">
            <TabsTrigger value="gallery" data-testid="tab-gallery">Gallery</TabsTrigger>
            <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
          </TabsList>

          <TabsContent value="gallery" data-testid="tab-content-gallery">
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Add New Image</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <Label htmlFor="image_url_direct">Image URL (Direct link)</Label>
                    <Input
                      id="image_url_direct"
                      placeholder="https://esempio.com/immagine.jpg o lascia vuoto per upload"
                      value={newImage.image_url}
                      onChange={(e) => setNewImage(prev => ({ ...prev, image_url: e.target.value }))}
                      data-testid="input-image-url"
                      className="mt-2"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Puoi incollare un URL diretto all'immagine oppure caricare un file qui sotto
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="file">Upload Image (opzionale)</Label>
                    <Input
                      id="file"
                      type="file"
                      accept="image/*"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                      data-testid="input-file"
                      className="mt-2"
                    />
                    <Button
                      onClick={handleFileUpload}
                      data-testid="button-upload"
                      className="mt-4"
                    >
                      Upload File
                    </Button>
                  </div>

                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Select
                      onValueChange={(value) => setNewImage(prev => ({ ...prev, category: value }))}
                      value={newImage.category}
                    >
                      <SelectTrigger data-testid="select-category" className="mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="wedding">Wedding</SelectItem>
                        <SelectItem value="family">Family</SelectItem>
                        <SelectItem value="events">Events</SelectItem>
                        <SelectItem value="portraits">Portraits</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="alt_text">Alt Text</Label>
                    <Input
                      id="alt_text"
                      value={newImage.alt_text}
                      onChange={(e) => setNewImage(prev => ({ ...prev, alt_text: e.target.value }))}
                      data-testid="input-alt-text"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="order">Order</Label>
                    <Input
                      id="order"
                      type="number"
                      value={newImage.order}
                      onChange={(e) => setNewImage(prev => ({ ...prev, order: parseInt(e.target.value) }))}
                      data-testid="input-order"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="caption_it">Caption (IT)</Label>
                    <Input
                      id="caption_it"
                      value={newImage.caption_it}
                      onChange={(e) => setNewImage(prev => ({ ...prev, caption_it: e.target.value }))}
                      data-testid="input-caption-it"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="caption_en">Caption (EN)</Label>
                    <Input
                      id="caption_en"
                      value={newImage.caption_en}
                      onChange={(e) => setNewImage(prev => ({ ...prev, caption_en: e.target.value }))}
                      data-testid="input-caption-en"
                      className="mt-2"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="is_featured"
                      checked={newImage.is_featured}
                      onChange={(e) => setNewImage(prev => ({ ...prev, is_featured: e.target.checked }))}
                      data-testid="checkbox-featured"
                      className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                    />
                    <Label htmlFor="is_featured" className="cursor-pointer">
                      Featured (mostra in Hero Slideshow)
                    </Label>
                  </div>
                </div>

                <Button
                  onClick={handleAddImage}
                  data-testid="button-add-image"
                  className="mt-6"
                  disabled={!newImage.image_url}
                >
                  Add Image to Gallery
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Gallery Images ({galleryImages.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {galleryImages.map((image) => (
                    <div key={image.id} className="border rounded p-2" data-testid={`gallery-item-${image.id}`}>
                      <img
                        src={image.image_url}
                        alt={image.alt_text}
                        className="w-full h-40 object-cover rounded mb-2"
                      />
                      <p className="text-xs text-muted-foreground mb-1">
                        <strong>Category:</strong> {image.category}
                      </p>
                      <p className="text-xs text-muted-foreground mb-2">
                        <strong>Order:</strong> {image.order}
                      </p>
                      {image.is_featured && (
                        <p className="text-xs font-semibold text-primary mb-2">
                          ⭐ Featured
                        </p>
                      )}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEditImage(image)}
                          data-testid={`button-edit-${image.id}`}
                          className="w-full"
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDeleteImage(image.id)}
                          data-testid={`button-delete-${image.id}`}
                          className="w-full"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contacts" data-testid="tab-content-contacts">
            <Card>
              <CardHeader>
                <CardTitle>Contact Messages ({contacts.length})</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {contacts.map((contact) => (
                    <div
                      key={contact.id}
                      className="border rounded p-4"
                      data-testid={`contact-item-${contact.id}`}
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-2">
                        <div>
                          <p className="text-sm">
                            <strong>Name:</strong> {contact.name}
                          </p>
                          <p className="text-sm">
                            <strong>Email:</strong> {contact.email}
                          </p>
                          {contact.phone && (
                            <p className="text-sm">
                              <strong>Phone:</strong> {contact.phone}
                            </p>
                          )}
                        </div>
                        <div>
                          {contact.service && (
                            <p className="text-sm">
                              <strong>Service:</strong> {contact.service}
                            </p>
                          )}
                          <p className="text-sm">
                            <strong>Date:</strong> {new Date(contact.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="mt-2">
                        <p className="text-sm">
                          <strong>Message:</strong>
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">{contact.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Edit Image Dialog */}
        <Dialog open={!!editingImage} onOpenChange={() => setEditingImage(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit Image</DialogTitle>
            </DialogHeader>
            {editingImage && (
              <div className="space-y-4">
                <div>
                  <img
                    src={editingImage.image_url}
                    alt={editingImage.alt_text}
                    className="w-full h-48 object-cover rounded"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-alt-text">Alt Text</Label>
                  <Input
                    id="edit-alt-text"
                    value={editingImage.alt_text || ''}
                    onChange={(e) => setEditingImage({ ...editingImage, alt_text: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-caption-it">Caption (IT)</Label>
                  <Input
                    id="edit-caption-it"
                    value={editingImage.caption_it || ''}
                    onChange={(e) => setEditingImage({ ...editingImage, caption_it: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-caption-en">Caption (EN)</Label>
                  <Input
                    id="edit-caption-en"
                    value={editingImage.caption_en || ''}
                    onChange={(e) => setEditingImage({ ...editingImage, caption_en: e.target.value })}
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="edit-order">Order</Label>
                  <Input
                    id="edit-order"
                    type="number"
                    value={editingImage.order || 0}
                    onChange={(e) => setEditingImage({ ...editingImage, order: parseInt(e.target.value) })}
                    className="mt-2"
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="edit-featured"
                    checked={editingImage.is_featured || false}
                    onChange={(e) => setEditingImage({ ...editingImage, is_featured: e.target.checked })}
                    className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                  />
                  <Label htmlFor="edit-featured" className="cursor-pointer">
                    Featured (mostra in Hero Slideshow)
                  </Label>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingImage(null)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateImage}>
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Admin;
